import React, { useMemo, useState } from "react";
import {
  Card,
  CardContent,
  Stack,
  Typography,
  Button,
  IconButton,
  Dialog,
  Box,
  Menu,
  MenuItem,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList,
} from "recharts";
import { Download, Maximize2, X, MoreVertical, Factory } from "lucide-react";
import useApiEp from "../../hooks/useApiEp";
import { exportToExcel } from "../../utils/util";

const SECTOR_COLORS = [
  "#2563EB",
  "#7C3AED",
  "#0284C7",
  "#10B981",
  "#F59E0B",
  "#EC4899",
  "#8B5CF6",
  "#06B6D4",
];

const FALLBACK_SECTORS = [
  { industrySector: "Machinery & Equipment", count: 480 },
  { industrySector: "Automotive & Tools", count: 340 },
  { industrySector: "Electrical & Electronics", count: 290 },
  { industrySector: "Steel & Metallurgy", count: 210 },
  { industrySector: "Robotics & Automation", count: 175 },
  { industrySector: "Chemicals & Plastics", count: 120 },
];

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <Box
        sx={{
          backgroundColor: "#1E293B",
          color: "#FFFFFF",
          p: 1.5,
          borderRadius: "8px",
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
        }}
      >
        <Typography variant="subtitle2" fontWeight="700">
          {data.industrySector}
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.8, color: "#60A5FA" }}>
          Registrations: <strong>{data.count.toLocaleString()}</strong>
        </Typography>
      </Box>
    );
  }
  return null;
};

const IndustrySectorwiseCard = () => {
  const { data: sectorwiseData } = useApiEp(
    `/user/get-industry-sectorwise-registrations`
  );
  const [isFullscreen, setFullscreen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const isMenuOpen = Boolean(anchorEl);

  const handleMenuClick = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const dataBySector = useMemo(() => {
    if (
      sectorwiseData &&
      Array.isArray(sectorwiseData.registrations) &&
      sectorwiseData.registrations.length > 0
    ) {
      return sectorwiseData.registrations
        .filter(
          (item) =>
            typeof item.industrySector === "string" &&
            item.industrySector.trim()
        )
        .map((item) => ({
          industrySector: item.industrySector.trim(),
          count: Number(item.count) || 0,
        }))
        .sort((a, b) => b.count - a.count);
    }
    return FALLBACK_SECTORS;
  }, [sectorwiseData]);

  const handleExport = () => {
    handleMenuClose();
    exportToExcel(
      dataBySector.map(({ industrySector, count }) => ({
        "Industry Sector": industrySector,
        Registrations: count,
      })),
      "Registrations_by_Industry_Sector"
    );
  };

  const renderChart = (isModal = false) => {
    const dataList = isModal ? dataBySector : dataBySector.slice(0, 6);
    // Dynamic height calculation provides ample vertical line spacing per industry sector bar
    const chartHeight = isModal
      ? Math.max(500, dataList.length * 52)
      : Math.max(260, dataList.length * 42);

    return (
      <Box sx={{ width: "100%", height: chartHeight }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={dataList}
            layout="vertical"
            margin={{ top: 15, right: 40, left: 10, bottom: 20 }}
            barCategoryGap={18}
          >
            <XAxis
              type="number"
              axisLine={false}
              tickLine={false}
              tick={{ fill: "#64748B", fontSize: 11 }}
            />
            <YAxis
              dataKey="industrySector"
              type="category"
              axisLine={false}
              tickLine={false}
              tick={{ fill: "#1E293B", fontSize: 12, fontWeight: 600 }}
              width={185}
              interval={0}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(37, 99, 235, 0.04)" }} />
            <Bar dataKey="count" radius={[0, 8, 8, 0]} barSize={16}>
              {dataList.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={SECTOR_COLORS[index % SECTOR_COLORS.length]} />
              ))}
              <LabelList
                dataKey="count"
                position="right"
                style={{ fill: "#1E293B", fontWeight: 600, fontSize: 12 }}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
    );
  };

  return (
    <>
      <Card
        sx={{
          height: "100%",
          borderRadius: "16px",
          border: "1px solid #E7EAF0",
          boxShadow: "0px 1px 3px rgba(16, 24, 40, 0.05)",
        }}
      >
        <CardContent sx={{ p: 2.5 }}>
          {/* Card Header */}
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: "10px",
                  backgroundColor: "#F3E8FF",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#7C3AED",
                }}
              >
                <Factory size={20} />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight="700" color="#1E293B" sx={{ fontSize: "1.05rem" }}>
                  Registrations by Industry Sector
                </Typography>
                <Typography variant="caption" color="#64748B">
                  Attendee distribution across sectors
                </Typography>
              </Box>
            </Stack>

            <Stack direction="row" spacing={0.5}>
              <IconButton size="small" onClick={() => setFullscreen(true)}>
                <Maximize2 size={16} color="#64748B" />
              </IconButton>
              <IconButton size="small" onClick={handleMenuClick}>
                <MoreVertical size={16} color="#64748B" />
              </IconButton>
            </Stack>
          </Stack>

          {/* Menu */}
          <Menu anchorEl={anchorEl} open={isMenuOpen} onClose={handleMenuClose}>
            <MenuItem onClick={handleExport} sx={{ fontSize: "0.85rem", gap: 1 }}>
              <Download size={14} /> Export to Excel
            </MenuItem>
          </Menu>

          {/* Chart View */}
          <Box sx={{ flexGrow: 1, width: "100%", pt: 1, overflow: "hidden" }}>
            {renderChart(false)}
          </Box>
        </CardContent>
      </Card>

      {/* Fullscreen Dialog */}
      <Dialog fullScreen open={isFullscreen} onClose={() => setFullscreen(false)}>
        <Box sx={{ p: 3, height: "100%", backgroundColor: "#F8FAFC", overflowY: "auto" }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h5" fontWeight="700" color="#1E293B">
              Registrations by Industry Sector (Full View)
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" startIcon={<Download size={16} />} onClick={handleExport}>
                Export Excel
              </Button>
              <IconButton onClick={() => setFullscreen(false)}>
                <X size={24} />
              </IconButton>
            </Stack>
          </Stack>
          <Card
            sx={{
              borderRadius: "16px",
              p: 3.5,
              minHeight: "calc(100vh - 140px)",
              overflowY: "auto",
            }}
          >
            {renderChart(true)}
          </Card>
        </Box>
      </Dialog>
    </>
  );
};

export default IndustrySectorwiseCard;
